import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

export interface AuthState {
  authenticated: boolean;
  enrolled: boolean;
}

interface Ceremony<T> {
  ceremonyId: string;
  options: T;
}

export interface PasskeySummary {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  backedUp: boolean;
  deviceType: string;
  current: boolean;
}

export interface DeviceAuthorization {
  requestId: string;
  verifier: string;
  expiresAt: string;
}

export type DeviceAuthorizationStatus = "pending" | "complete" | "unavailable";

export function captureEnrollmentToken(): string | null {
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  const token = params.get("enroll");
  if (token) history.replaceState(null, "", `${location.pathname}${location.search}`);
  return token;
}

export function passkeysSupported(): boolean {
  return window.isSecureContext && browserSupportsWebAuthn();
}

export async function loadAuthState(): Promise<AuthState> {
  const response = await fetch("/api/auth/state", { cache: "no-store" });
  if (!response.ok) throw new Error("Time Tile could not check its lock.");
  return response.json() as Promise<AuthState>;
}

export async function enrollPasskey(token: string, label: string): Promise<void> {
  const started = await post<{ token: string }, Ceremony<PublicKeyCredentialCreationOptionsJSON>>(
    "/api/auth/registration/options",
    { token },
  );
  const response = await startRegistration({ optionsJSON: started.options });
  await post("/api/auth/registration/verify", {
    ceremonyId: started.ceremonyId,
    token,
    label,
    response,
  });
}

export async function authenticatePasskey(): Promise<void> {
  const started = await post<Record<string, never>, Ceremony<PublicKeyCredentialRequestOptionsJSON>>(
    "/api/auth/authentication/options",
    {},
  );
  const response = await startAuthentication({ optionsJSON: started.options });
  await post("/api/auth/authentication/verify", {
    ceremonyId: started.ceremonyId,
    response,
  });
}

export async function startDeviceAuthorization(): Promise<DeviceAuthorization> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
  const digest = new Uint8Array(await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  ));
  const requestId = [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return {
    requestId,
    verifier,
    expiresAt: new Date(Date.now() + 5 * 60 * 1_000).toISOString(),
  };
}

export async function approveDeviceAuthorization(requestId: string): Promise<void> {
  await post("/api/auth/device/approve", { requestId });
}

export async function completeDeviceAuthorization(
  verifier: string,
): Promise<DeviceAuthorizationStatus> {
  const response = await fetch("/api/auth/device/complete", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ verifier }),
  });
  if (response.status === 202) return "pending";
  if (response.status === 410) return "unavailable";
  if (!response.ok) {
    const result = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new Error(typeof result?.error === "string" ? result.error : "Mac approval failed.");
  }
  return "complete";
}

export async function logout(): Promise<void> {
  await post("/api/auth/logout", {});
}

export async function listPasskeys(): Promise<PasskeySummary[]> {
  const response = await fetch("/api/auth/passkeys", { cache: "no-store" });
  if (!response.ok) throw new Error("Could not load passkeys.");
  return ((await response.json()) as { passkeys: PasskeySummary[] }).passkeys;
}

export async function addPasskey(label: string): Promise<void> {
  const started = await post<Record<string, never>, Ceremony<PublicKeyCredentialCreationOptionsJSON>>(
    "/api/auth/passkeys/registration/options",
    {},
  );
  const response = await startRegistration({ optionsJSON: started.options });
  await post("/api/auth/passkeys/registration/verify", {
    ceremonyId: started.ceremonyId,
    label,
    response,
  });
}

export async function removePasskey(id: string): Promise<void> {
  const response = await fetch("/api/auth/passkeys", {
    method: "DELETE",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new Error(typeof result?.error === "string" ? result.error : "Could not remove passkey.");
  }
}

async function post<TBody, TResult = unknown>(path: string, body: TBody): Promise<TResult> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new Error(typeof result?.error === "string" ? result.error : "Passkey request failed.");
  }
  return response.status === 204 ? undefined as TResult : response.json() as Promise<TResult>;
}

export function passkeyError(error: unknown, action: "enroll" | "authenticate"): string {
  if (error instanceof Error && error.name === "NotAllowedError") {
    return action === "enroll" ? "No passkey was created." : "Passkey sign-in was cancelled.";
  }
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error.trim()) return error.trim();
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.trim()
  ) return error.message.trim();
  return action === "enroll" ? "Passkey setup failed." : "Passkey sign-in failed.";
}
